import React from 'react';

import {useIntl} from 'react-intl';
import styled from 'styled-components';

import {dimensions} from '@stryd/react-ui';

import {usePageRegionData} from 'src/contexts';
import {VariantPricing} from 'src/features/pricing/helpers';
import {usePriceFormatters} from 'src/hooks/usePriceFormatters';

const columnLayoutBreakpoint = dimensions.breakpoints.tabletLandscape;

const ClipsSectionContainer = styled.div`
  margin: 24px 0;
  padding: 0 12px;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    padding: 0 30px;
  }
`;

const ClipsCard = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 24px;
  gap: 24px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

  @media screen and (max-width: ${columnLayoutBreakpoint}) {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
`;

const ClipsLeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ClipsCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ClipsCheckbox = styled.input`
  width: 20px;
  height: 20px;
  accent-color: #41a4ff;
  cursor: pointer;
`;

const ClipsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #000;
  margin: 0;
`;

const ClipsDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const ClipsFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ClipsFeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
`;

const ClipsCheckmarkIcon = styled.span`
  color: #41a4ff;
  margin-right: 8px;
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 1px;
`;

const ClipsLearnMore = styled.a`
  color: #41a4ff;
  text-decoration: underline;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`;

const ClipsRightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
`;

const ClipsPricingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ClipsCurrentPrice = styled.span`
  font-size: 24px;
  font-weight: 600;
  color: #000;
`;

const ClipsOldPrice = styled.span`
  font-size: 18px;
  font-weight: 400;
  color: #999;
  text-decoration: line-through;
`;

const ClipsSavingsBadge = styled.div`
  background: #f4e4c1;
  color: #8b4513;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
`;

const ClipsImage = styled.div`
  width: 200px;
  height: 150px;
  background-image: url('/images/stryd5/STRYD_Array A.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  flex-shrink: 0;

  @media screen and (max-width: ${columnLayoutBreakpoint}) {
    width: 150px;
    height: 112px;
  }
`;

interface ClipsSectionProps {
  includeClips: boolean;
  onClipsChange: (checked: boolean) => void;
  clipPricing: VariantPricing;
  clipOriginalPricing: VariantPricing;
}

const ClipsSection: React.FC<ClipsSectionProps> = ({
  includeClips,
  onClipsChange,
  clipPricing,
  clipOriginalPricing,
}) => {
  const {locale} = usePageRegionData();
  const {formatOneTimePrice} = usePriceFormatters({
    locale,
  });
  const intl = useIntl();

  const priceDifference = {
    ...clipOriginalPricing.oneTimePricesTotal,
    unit_amount:
      clipOriginalPricing.oneTimePricesTotal.unit_amount -
      clipPricing.oneTimePricesTotal.unit_amount,
  };
  return (
    <ClipsSectionContainer>
      <ClipsCard>
        <ClipsImage />
        <ClipsLeftSection>
          <ClipsCheckboxContainer>
            <ClipsCheckbox
              type="checkbox"
              id="include-clips"
              checked={includeClips}
              onChange={(e) => onClipsChange(e.target.checked)}
            />
            <ClipsTitle>
              {intl.formatMessage({id: 'store5.clips.Title'})}
            </ClipsTitle>
          </ClipsCheckboxContainer>
          <ClipsDescription>
            {intl.formatMessage({id: 'store5.clips.Description'})}
          </ClipsDescription>
          <ClipsFeatures>
            <ClipsFeatureItem>
              <ClipsCheckmarkIcon>✓</ClipsCheckmarkIcon>
              <span>{intl.formatMessage({id: 'store5.clips.Feature1'})}</span>
            </ClipsFeatureItem>
            <ClipsFeatureItem>
              <ClipsCheckmarkIcon>✓</ClipsCheckmarkIcon>
              <span>{intl.formatMessage({id: 'store5.clips.Feature2'})}</span>
            </ClipsFeatureItem>
          </ClipsFeatures>
          {/* <ClipsLearnMore href="#" onClick={(e) => e.preventDefault()}>
            Learn more
          </ClipsLearnMore> */}
        </ClipsLeftSection>
        <ClipsRightSection>
          <ClipsPricingContainer>
            <ClipsCurrentPrice>
              {formatOneTimePrice(clipPricing.oneTimePricesTotal)}
            </ClipsCurrentPrice>
            <ClipsOldPrice>
              {formatOneTimePrice(clipOriginalPricing.oneTimePricesTotal)}
            </ClipsOldPrice>
            <ClipsSavingsBadge>
              {formatOneTimePrice(priceDifference)} OFF
            </ClipsSavingsBadge>
          </ClipsPricingContainer>
        </ClipsRightSection>
      </ClipsCard>
    </ClipsSectionContainer>
  );
};

export default ClipsSection;
